import { ButtonPrimary, ButtonSecondary, Colors, ModalDialog, Text } from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { BulkAdjustmentFields } from './BulkAdjustmentFields';
import { BULK_PERCENTAGE_MAX, BULK_PERCENTAGE_MIN } from '../calibrationConstants';
import { MODAL_FOOTER, MODAL_PREVIEW_PANEL } from '../calibrationStyles';
import { computeBulkPreview } from '../utils/computeBulkPreview';

import type {
    CalibrationAdjustmentDirection,
    CalibrationReport,
    CalibrationReportFilters,
} from '@/types/calibration';
import type { FormEvent } from 'react';

import { useBulkAdjustCalibration } from '@/api/calibration';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';

export interface BulkAdjustmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    report: CalibrationReport;
    filters: CalibrationReportFilters;
    cycleEvaluationId: string;
    language: string;
}

const parsePercentage = (raw: string): number | null => {
    if (raw.trim() === '') {
        return null;
    }

    const parsed = Number(raw.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
};

const isValidPercentage = (percentage: number | null): percentage is number =>
    percentage !== null && percentage >= BULK_PERCENTAGE_MIN && percentage <= BULK_PERCENTAGE_MAX;

/**
 * Applies a percentage increase or decrease to the current score of the targeted managers'
 * employees, previewing the affected headcount client-side (`computeBulkPreview`, mirroring the
 * backend's own rounding) before sending `POST …/Bulk`.
 */
export const BulkAdjustmentModal = ({
    isOpen,
    onClose,
    report,
    filters,
    cycleEvaluationId,
    language,
}: BulkAdjustmentModalProps) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const bulkAdjust = useBulkAdjustCalibration(cycleEvaluationId);

    // Pre-selected with every manager in the currently filtered report: unchecking one narrows
    // the sweep to the rest, matching what the multi-select visibly shows as selected.
    const [managerIds, setManagerIds] = useState<string[]>(() =>
        report.managers.map((manager) => manager.managerId),
    );
    const [direction, setDirection] = useState<CalibrationAdjustmentDirection>('Increase');
    const [percentageInput, setPercentageInput] = useState('');

    const percentage = parsePercentage(percentageInput);
    const hasValidPercentage = isValidPercentage(percentage);

    const preview = useMemo(
        () =>
            computeBulkPreview({
                report,
                managerIds,
                direction,
                percentage: hasValidPercentage ? percentage : 0,
            }),
        [report, managerIds, direction, hasValidPercentage, percentage],
    );

    const canApply = hasValidPercentage && preview.affectedCount > 0 && !bulkAdjust.isPending;

    const toggleManager = (managerId: string) => {
        setManagerIds((previous) =>
            previous.includes(managerId)
                ? previous.filter((id) => id !== managerId)
                : [...previous, managerId],
        );
    };

    const handleClose = () => {
        setManagerIds(report.managers.map((manager) => manager.managerId));
        setDirection('Increase');
        setPercentageInput('');
        onClose();
    };

    const handleApply = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canApply) {
            return;
        }

        try {
            const response = await bulkAdjust.mutateAsync({
                managerIds,
                direction,
                percentage,
                filters,
            });
            showSuccessToast({
                message: t('calibration.bulk.success', { count: response.updatedCount }),
            });
            handleClose();
        } catch (error) {
            handleError({ error, data: { message: t('calibration.errors.bulkAdjustmentFailed') } });
        }
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={handleClose}
            title={t('calibration.bulk.modalTitle')}
            size={'md'}
            closeAriaLabel={t('common.close')}
            onSubmit={(event) => void handleApply(event)}
            backdropBlur
            dataTestId={'calibration-bulk-modal'}
            footer={
                <div className={MODAL_FOOTER}>
                    <ButtonSecondary
                        size={'sm'}
                        onClick={handleClose}
                        dataTestId={'calibration-bulk-cancel'}
                    >
                        {t('calibration.bulk.cancel')}
                    </ButtonSecondary>

                    <ButtonPrimary
                        type={'submit'}
                        size={'sm'}
                        disabled={!canApply}
                        loading={bulkAdjust.isPending}
                        // The library swaps the label for a spinner while applying; the name holds.
                        ariaLabel={t('calibration.bulk.confirm')}
                        dataTestId={'calibration-bulk-confirm'}
                    >
                        {t('calibration.bulk.confirm')}
                    </ButtonPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-5'}>
                <BulkAdjustmentFields
                    managers={report.managers}
                    selectedManagerIds={managerIds}
                    onToggleManager={toggleManager}
                    direction={direction}
                    onDirectionChange={setDirection}
                    percentageInput={percentageInput}
                    onPercentageChange={setPercentageInput}
                    language={language}
                />

                <div className={MODAL_PREVIEW_PANEL}>
                    <Text weight={'medium'}>{t('calibration.bulk.previewLabel')}</Text>

                    {/* Until the percentage is a usable one, the sweep would touch nobody:
                        announcing a headcount here would promise an effect the "Apply" button
                        cannot yet deliver. */}
                    <span data-testid={'calibration-bulk-preview'}>
                        <Text color={hasValidPercentage ? undefined : Colors.BASE_400}>
                            {hasValidPercentage
                                ? t('calibration.bulk.affectedCount', {
                                      count: preview.affectedCount,
                                  })
                                : t('calibration.bulk.previewHint')}
                        </Text>
                    </span>
                </div>
            </div>
        </ModalDialog>
    );
};
